import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AudioLines, Download, Loader2, Mic, Pause, Play, RotateCcw, Square, Upload, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import AccessGateModal from '@/components/AccessGateModal';
import OutputModeSelector, { type OutputMode } from '@/components/OutputModeSelector';
import LanguageSelector from '@/components/LanguageSelector';
import { SUPPORTED_LANGUAGES, getLanguageName } from '@/lib/languages';
import { supabase } from '@/integrations/supabase/client';
import type { AccessState } from '@/hooks/useMembershipStatus';
import { useLanguage } from '@/i18n/LanguageContext';
import { useTrialData } from '@/hooks/useTrialData';

type RecordingState = 'idle' | 'recording' | 'transcribing' | 'translating' | 'generating-audio' | 'ready';

interface VoiceTranslationProps {
  accessState: AccessState;
}

const MAX_AUDIO_MB = 20;
const MIN_RECORDING_SECONDS = 1;
const MIN_ACCEPTED_RECORDING_SECONDS = 0.8;

const getSupportedRecordingMimeType = () => {
  if (typeof MediaRecorder === 'undefined') return '';

  const candidates = [
    'audio/webm;codecs=opus',
    'audio/mp4',
    'audio/webm',
    'audio/ogg;codecs=opus',
  ];

  return candidates.find((candidate) => MediaRecorder.isTypeSupported(candidate)) ?? '';
};

const getBlobExtension = (mimeType: string) => {
  if (mimeType.includes('mp4')) return 'm4a';
  if (mimeType.includes('mpeg')) return 'mp3';
  if (mimeType.includes('ogg')) return 'ogg';
  if (mimeType.includes('wav')) return 'wav';
  return 'webm';
};

const blobToArrayBuffer = async (blob: Blob) => new Uint8Array(await blob.arrayBuffer());

const encodeWav = (audioBuffer: AudioBuffer) => {
  const channels = audioBuffer.numberOfChannels;
  const sampleRate = audioBuffer.sampleRate;
  const samples = audioBuffer.length;
  const bytesPerSample = 2;
  const blockAlign = channels * bytesPerSample;
  const buffer = new ArrayBuffer(44 + samples * blockAlign);
  const view = new DataView(buffer);

  const writeString = (offset: number, value: string) => {
    for (let i = 0; i < value.length; i += 1) {
      view.setUint8(offset + i, value.charCodeAt(i));
    }
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + samples * blockAlign, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, channels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, samples * blockAlign, true);

  let offset = 44;
  const channelData = Array.from({ length: channels }, (_, index) => audioBuffer.getChannelData(index));

  for (let i = 0; i < samples; i += 1) {
    for (let channel = 0; channel < channels; channel += 1) {
      const normalized = Math.max(-1, Math.min(1, channelData[channel][i] * 1.1));
      view.setInt16(offset, normalized < 0 ? normalized * 0x8000 : normalized * 0x7fff, true);
      offset += bytesPerSample;
    }
  }

  return new Blob([buffer], { type: 'audio/wav' });
};

const trimSilence = (audioBuffer: AudioBuffer) => {
  const threshold = 0.01;
  const channels = Array.from({ length: audioBuffer.numberOfChannels }, (_, index) => audioBuffer.getChannelData(index));
  let start = 0;
  let end = audioBuffer.length - 1;

  const isFrameSilent = (index: number) => channels.every((channel) => Math.abs(channel[index] ?? 0) < threshold);

  while (start < end && isFrameSilent(start)) start += 1;
  while (end > start && isFrameSilent(end)) end -= 1;

  const frameCount = Math.max(1, end - start + 1);
  const trimmed = new AudioBuffer({
    length: frameCount,
    numberOfChannels: audioBuffer.numberOfChannels,
    sampleRate: audioBuffer.sampleRate,
  });

  for (let channel = 0; channel < audioBuffer.numberOfChannels; channel += 1) {
    trimmed.copyToChannel(audioBuffer.getChannelData(channel).slice(start, end + 1), channel);
  }

  return trimmed;
};

const VoiceTranslation = ({ accessState }: VoiceTranslationProps) => {
  const { t } = useLanguage();
  const { isSubscribed } = useTrialData();
  const canTranslate = accessState === 'active' || accessState === 'trial';
  const [sourceLanguage, setSourceLanguage] = useState<string>('auto');
  const [targetLanguage, setTargetLanguage] = useState<string>('en');
  const [outputMode, setOutputMode] = useState<OutputMode>('text');
  const [recordingState, setRecordingState] = useState<RecordingState>('idle');
  const [showGateModal, setShowGateModal] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [translatedText, setTranslatedText] = useState('');
  const [detectedLanguage, setDetectedLanguage] = useState<string | null>(null);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [audioDuration, setAudioDuration] = useState<number | null>(null);
  const [capturedDuration, setCapturedDuration] = useState<number | null>(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [meterLevel, setMeterLevel] = useState(0);
  const [processingMessage, setProcessingMessage] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const requestIdRef = useRef(0);
  const recordingStartedAtRef = useRef<number | null>(null);
  const recordingStoppedAtRef = useRef<number | null>(null);
  const recordingDurationRef = useRef<number | null>(null);

  const waveformBars = useMemo(() => Array.from({ length: 24 }, (_, index) => index), []);
  const hasContent = Boolean(transcript.trim() || translatedText.trim() || selectedFileName || audioBlob || audioUrl);
  const requiresResetConfirmation = transcript.trim().length > 200 || translatedText.trim().length > 200 || Boolean(audioBlob);

  const cleanupPlaybackUrl = useCallback((url: string | null) => {
    if (url && url.startsWith('blob:')) URL.revokeObjectURL(url);
  }, []);

  const stopVisualizer = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    setMeterLevel(0);
  }, []);

  const stopRecordingClock = useCallback(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const updateRecordingClock = useCallback(() => {
    const startedAt = recordingStartedAtRef.current;
    if (!startedAt) {
      setRecordingSeconds(0);
      return;
    }
    const stoppedAt = recordingStoppedAtRef.current ?? performance.now();
    const durationSeconds = Math.max(0, (stoppedAt - startedAt) / 1000);
    setRecordingSeconds(durationSeconds);
  }, []);

  const stopMediaStream = useCallback(() => {
    mediaRecorderRef.current?.stream.getTracks().forEach((track) => track.stop());
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    mediaRecorderRef.current = null;
    mediaStreamRef.current = null;
    analyserRef.current = null;
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => undefined);
      audioContextRef.current = null;
    }
    stopVisualizer();
    stopRecordingClock();
  }, [stopRecordingClock, stopVisualizer]);

  const resetState = useCallback(() => {
    requestIdRef.current += 1;
    stopMediaStream();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    cleanupPlaybackUrl(audioUrl);
    setRecordingState('idle');
    setTranscript('');
    setTranslatedText('');
    setDetectedLanguage(null);
    setSelectedFileName(null);
    setAudioBlob(null);
    setAudioUrl(null);
    setAudioDuration(null);
    setCapturedDuration(null);
    setRecordingSeconds(0);
    setAudioPlaying(false);
    setProcessingMessage(null);
    setShowResetConfirm(false);
    recordingStartedAtRef.current = null;
    recordingStoppedAtRef.current = null;
    recordingDurationRef.current = null;
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [audioUrl, cleanupPlaybackUrl, stopMediaStream]);

  const requestReset = useCallback(() => {
    if (!hasContent) return;
    if (requiresResetConfirmation) {
      setShowResetConfirm(true);
      return;
    }
    resetState();
  }, [hasContent, requiresResetConfirmation, resetState]);

  const updateMeter = useCallback(() => {
    const analyser = analyserRef.current;
    if (!analyser) return;
    const buffer = new Uint8Array(analyser.frequencyBinCount);
    const read = () => {
      analyser.getByteFrequencyData(buffer);
      const average = buffer.reduce((sum, value) => sum + value, 0) / buffer.length;
      setMeterLevel(average / 255);
      animationFrameRef.current = requestAnimationFrame(read);
    };
    read();
  }, []);

  const ensureAudioPreview = useCallback((blob: Blob) => {
    cleanupPlaybackUrl(audioUrl);
    const nextUrl = URL.createObjectURL(blob);
    setAudioBlob(blob);
    setAudioUrl(nextUrl);
    return nextUrl;
  }, [audioUrl, cleanupPlaybackUrl]);

  const getAudioDuration = useCallback(async (blob: Blob) => {
    const previewUrl = URL.createObjectURL(blob);
    try {
      const duration = await new Promise<number>((resolve, reject) => {
        const audio = document.createElement('audio');
        audio.preload = 'metadata';
        audio.onloadedmetadata = () => resolve(Number.isFinite(audio.duration) ? audio.duration : 0);
        audio.onerror = () => reject(new Error('AUDIO_METADATA_FAILED'));
        audio.src = previewUrl;
      });
      return duration;
    } finally {
      URL.revokeObjectURL(previewUrl);
    }
  }, []);

  const resolveReliableDuration = useCallback((...candidates: Array<number | null | undefined>) => {
    const valid = candidates.filter((value): value is number => Number.isFinite(value) && value > 0);
    if (!valid.length) return null;
    return Math.max(...valid);
  }, []);

  const normalizeRecordedAudio = useCallback(async (blob: Blob) => {
    const AudioContextCtor = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextCtor) return { blob, mimeType: blob.type || 'audio/webm', extension: getBlobExtension(blob.type || 'audio/webm') };

    const audioContext = new AudioContextCtor();
    try {
      const sourceBuffer = await blob.arrayBuffer();
      const decoded = await audioContext.decodeAudioData(sourceBuffer.slice(0));
      const trimmed = trimSilence(decoded);
      const wavBlob = encodeWav(trimmed);
      return { blob: wavBlob, mimeType: 'audio/wav', extension: 'wav', duration: trimmed.duration };
    } catch (error) {
      console.warn('Voice normalization fallback:', error);
      return { blob, mimeType: blob.type || 'audio/webm', extension: getBlobExtension(blob.type || 'audio/webm') };
    } finally {
      audioContext.close().catch(() => undefined);
    }
  }, []);

  const getTranscriptionErrorMessage = useCallback((errorMessage: string) => {
    const normalized = errorMessage.toLowerCase();
    if (normalized.includes('trial')) return t('text.trialEnded');
    if (normalized.includes('recording_empty')) return t('voice.recordingEmpty');
    if (normalized.includes('audio_too_short') || normalized.includes('recording_too_short') || normalized.includes('too short')) return t('voice.recordingTooShort');
    if (normalized.includes('unsupported_audio_format') || normalized.includes('unsupported audio format')) return t('voice.unsupportedFormat');
    if (normalized.includes('no speech') || normalized.includes('could not detect speech') || normalized.includes('empty transcription')) return t('voice.noSpeechDetected');
    if (normalized.includes('invalid_api_key') || normalized.includes('provider_auth_failed')) return t('voice.invalidApiConfiguration');
    if (normalized.includes('openai_api_key is not configured')) return t('voice.missingOpenAIKey');
    if (normalized.includes('provider_quota_exceeded') || normalized.includes('quota')) return t('voice.quotaExceeded');
    if (normalized.includes('provider_rate_limited') || normalized.includes('429')) return t('voice.rateLimited');
    if (normalized.includes('provider_unavailable') || normalized.includes('transcription_provider_failed') || normalized.includes('failed to fetch') || normalized.includes('networkerror')) return t('voice.serviceFallback');
    return t('voice.transcriptionHelp');
  }, [t]);

  const transcribeAudio = useCallback(async (blob: Blob, fileName?: string, durationHint?: number | null) => {
    const currentRequestId = ++requestIdRef.current;
    setRecordingState('transcribing');
    setProcessingMessage(t('voice.processingAudio'));

    try {
      const normalizedAudio = await normalizeRecordedAudio(blob);
      const [rawDuration, processedDuration] = await Promise.all([
        getAudioDuration(blob).catch(() => null),
        normalizedAudio.blob === blob ? Promise.resolve(normalizedAudio.duration ?? null) : getAudioDuration(normalizedAudio.blob).catch(() => null),
      ]);
      const reliableDuration = resolveReliableDuration(durationHint, rawDuration);
      const advisoryDuration = resolveReliableDuration(reliableDuration, processedDuration, normalizedAudio.duration);

      console.info('Voice duration diagnostics', {
        fileName: fileName ?? `voice-input.${normalizedAudio.extension}`,
        originalSizeBytes: blob.size,
        normalizedSizeBytes: normalizedAudio.blob.size,
        timerDurationSeconds: durationHint ?? null,
        rawDurationSeconds: rawDuration,
        normalizedDurationSeconds: normalizedAudio.duration ?? null,
        processedDurationSeconds: processedDuration,
        reliableDurationSeconds: reliableDuration,
        advisoryDurationSeconds: advisoryDuration,
        originalMimeType: blob.type || 'audio/webm',
        normalizedMimeType: normalizedAudio.mimeType,
      });

      if (blob.size <= 0 || normalizedAudio.blob.size <= 0) {
        throw new Error('RECORDING_EMPTY');
      }

      if (reliableDuration !== null && reliableDuration < MIN_ACCEPTED_RECORDING_SECONDS) {
        throw new Error('RECORDING_TOO_SHORT');
      }

      console.info('Voice transcription request', {
        fileName: fileName ?? `voice-input.${normalizedAudio.extension}`,
        sizeBytes: normalizedAudio.blob.size,
        durationSeconds: advisoryDuration,
        mimeType: normalizedAudio.mimeType,
      });

      const formData = new FormData();
      formData.append('audio', normalizedAudio.blob, fileName ?? `voice-input.${normalizedAudio.extension}`);
      formData.append('sourceLanguage', sourceLanguage);
      formData.append('mimeType', normalizedAudio.mimeType);
      if (advisoryDuration !== null) {
        formData.append('durationSeconds', advisoryDuration.toString());
      }

      const session = await supabase.auth.getSession();
      const accessToken = session.data.session?.access_token;

      setProcessingMessage(t('voice.uploadingAudio'));
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/transcribe-voice`, {
        method: 'POST',
        headers: {
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: formData,
      });

      setProcessingMessage(t('voice.transcribing'));
      const data = await response.json().catch(() => ({}));
      if (currentRequestId !== requestIdRef.current) return;
      if (!response.ok || data?.error) throw new Error(data?.error || 'TRANSCRIPTION_FAILED');
      if (!data.transcript?.trim()) throw new Error('NO_SPEECH_DETECTED');

      setTranscript(data.transcript || '');
      setDetectedLanguage(data.detectedLanguage || null);
      setAudioDuration(data.audioDurationSeconds ?? advisoryDuration);
      setCapturedDuration(advisoryDuration);
      setRecordingState('ready');
      setProcessingMessage(null);
      toast.success(t('voice.transcriptionReady'));
    } catch (error: any) {
      if (currentRequestId !== requestIdRef.current) return;
      setRecordingState('idle');
      setProcessingMessage(null);
      toast.error(getTranscriptionErrorMessage(error.message || 'TRANSCRIPTION_FAILED'));
    }
  }, [getAudioDuration, getTranscriptionErrorMessage, normalizeRecordedAudio, resolveReliableDuration, sourceLanguage, t]);

  const generatePremiumAudio = useCallback(async (text: string) => {
    const session = await supabase.auth.getSession();
    const accessToken = session.data.session?.access_token;
    const translationId = (window as any).__lastVoiceTranslationId as string | undefined;
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-translation-audio`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify({
        text,
        targetLanguage,
        translationId,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData?.error || 'AUDIO_GENERATION_FAILED');
    }

    const blob = await response.blob();
    const nextUrl = ensureAudioPreview(blob);
    return nextUrl;
  }, [audioDuration, detectedLanguage, ensureAudioPreview, outputMode, selectedFileName, sourceLanguage, targetLanguage, transcript]);

  const handleTranslate = useCallback(async () => {
    if (!canTranslate) {
      setShowGateModal(true);
      return;
    }
    if (!transcript.trim()) {
      toast.error(t('voice.enterTranscript'));
      return;
    }

    const currentRequestId = ++requestIdRef.current;
    setRecordingState('translating');

    try {
      const { data, error } = await supabase.functions.invoke('translate-text', {
        body: {
          text: transcript,
          sourceLanguage: sourceLanguage === 'auto' ? 'auto-detect' : sourceLanguage,
          targetLanguage,
          outputMode,
          isVoiceTranslation: true,
          sourceFilename: selectedFileName,
          audioDurationSeconds: audioDuration,
        },
      });
      if (currentRequestId !== requestIdRef.current) return;
      if (error || data?.error) throw new Error(data?.error || error?.message || 'TRANSLATION_FAILED');

      setTranslatedText(data.translatedText || '');
      setDetectedLanguage(data.detectedLanguage || detectedLanguage);
      (window as any).__lastVoiceTranslationId = data.translationId ?? undefined;

      if ((outputMode === 'voice' || outputMode === 'both') && isSubscribed) {
        setRecordingState('generating-audio');
        await generatePremiumAudio(data.translatedText || '');
      }

      setRecordingState('ready');
      toast.success(t('text.translationComplete'));
    } catch (error: any) {
      if (currentRequestId !== requestIdRef.current) return;
      setRecordingState('ready');
      const message = error.message || 'TRANSLATION_FAILED';
      if (message.includes('MEMBERSHIP_PENDING')) toast.error(t('membership.pending'));
      else if (message.includes('MEMBERSHIP_BLOCKED')) toast.error(t('membership.blocked'));
      else if (message.toLowerCase().includes('trial')) toast.error(t('text.trialEnded'));
      else if (message.includes('429')) toast.error(t('voice.rateLimited'));
      else toast.error(t('text.somethingWentWrong'));
    }
  }, [audioDuration, canTranslate, detectedLanguage, generatePremiumAudio, isSubscribed, outputMode, selectedFileName, sourceLanguage, t, targetLanguage, transcript]);

  const startRecording = useCallback(async () => {
    if (!canTranslate) {
      setShowGateModal(true);
      return;
    }

    try {
      resetState();
      if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
        toast.error(t('voice.recordingUnsupported'));
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      mediaStreamRef.current = stream;
      const mimeType = getSupportedRecordingMimeType();
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 64;
      source.connect(analyser);
      analyserRef.current = analyser;
      updateMeter();

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };
      recorder.onstop = async () => {
        try {
          const stoppedAt = recordingStoppedAtRef.current ?? performance.now();
          const startedAt = recordingStartedAtRef.current;
          const timerDuration = startedAt ? Math.max(0, (stoppedAt - startedAt) / 1000) : null;
          recordingDurationRef.current = timerDuration;
          setRecordingSeconds(timerDuration ?? 0);
          setCapturedDuration(timerDuration);

          const blob = new Blob(audioChunksRef.current, { type: recorder.mimeType || 'audio/webm' });
          console.info('Voice recording captured', {
            sizeBytes: blob.size,
            timerDurationSeconds: timerDuration,
            mimeType: recorder.mimeType || 'audio/webm',
          });

          if (!blob.size) {
            toast.error(t('voice.recordingEmpty'));
            setRecordingState('idle');
            setProcessingMessage(null);
            return;
          }

          ensureAudioPreview(blob);
          await transcribeAudio(blob, `voice-input.${getBlobExtension(recorder.mimeType || 'audio/webm')}`, timerDuration);
        } finally {
          stopMediaStream();
        }
      };

      recorder.start(250);
      const startedAt = performance.now();
      recordingStartedAtRef.current = startedAt;
      recordingStoppedAtRef.current = null;
      recordingDurationRef.current = null;
      setRecordingState('recording');
      setProcessingMessage(t('voice.recording'));
      setRecordingSeconds(0);
      timerRef.current = window.setInterval(updateRecordingClock, 200);
    } catch {
      toast.error(t('voice.micDenied'));
      resetState();
    }
  }, [canTranslate, ensureAudioPreview, resetState, t, transcribeAudio, updateMeter, updateRecordingClock, stopMediaStream]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      recordingStoppedAtRef.current = performance.now();
      updateRecordingClock();
      stopVisualizer();
      stopRecordingClock();
      setProcessingMessage(t('voice.processingAudio'));
      mediaRecorderRef.current.stop();
    }
  }, [stopRecordingClock, stopVisualizer, t, updateRecordingClock]);

  const handleAudioUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_AUDIO_MB * 1024 * 1024) {
      toast.error(t('voice.fileTooLarge'));
      return;
    }

    resetState();
    setSelectedFileName(file.name);
    const blob = file.slice(0, file.size, file.type || 'audio/mpeg');
    console.info('Voice upload selected', {
      fileName: file.name,
      sizeBytes: file.size,
      mimeType: file.type || 'audio/mpeg',
    });
    ensureAudioPreview(blob);
    await transcribeAudio(blob, file.name, null);
  }, [ensureAudioPreview, resetState, t, transcribeAudio]);

  const handlePlayToggle = useCallback(async () => {
    const shouldUseBrowserSpeech = !isSubscribed || !audioUrl;
    if (shouldUseBrowserSpeech) {
      if (!translatedText.trim()) return;
      if (!('speechSynthesis' in window)) {
        toast.error(t('text.ttsNotSupported'));
        return;
      }
      if (audioPlaying) {
        window.speechSynthesis.cancel();
        setAudioPlaying(false);
        return;
      }
      const utterance = new SpeechSynthesisUtterance(translatedText);
      utterance.lang = targetLanguage;
      utterance.onend = () => setAudioPlaying(false);
      utterance.onerror = () => setAudioPlaying(false);
      setAudioPlaying(true);
      window.speechSynthesis.speak(utterance);
      return;
    }

    if (!audioRef.current) return;
    if (audioPlaying) {
      audioRef.current.pause();
      setAudioPlaying(false);
      return;
    }
    await audioRef.current.play();
    setAudioPlaying(true);
  }, [audioPlaying, audioUrl, isSubscribed, t, targetLanguage, translatedText]);

  const handleDownload = useCallback(() => {
    if (!isSubscribed || !audioUrl || !audioBlob) {
      toast.error(t('voice.premiumDownloadOnly'));
      return;
    }
    const link = document.createElement('a');
    link.href = audioUrl;
    link.download = 'translation-audio.mp3';
    link.click();
    toast.success(t('voice.downloadReady'));
  }, [audioBlob, audioUrl, isSubscribed, t]);

  useEffect(() => {
    return () => {
      stopMediaStream();
      cleanupPlaybackUrl(audioUrl);
      if ('speechSynthesis' in window) window.speechSynthesis.cancel();
    };
  }, [audioUrl, cleanupPlaybackUrl, stopMediaStream]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onEnded = () => setAudioPlaying(false);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('pause', onEnded);
    return () => {
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('pause', onEnded);
    };
  }, [audioUrl]);

  const statusText = processingMessage ?? (recordingState === 'recording'
    ? t('voice.recording')
    : recordingState === 'transcribing'
      ? t('voice.transcribing')
      : recordingState === 'translating'
        ? t('text.translating')
        : recordingState === 'generating-audio'
          ? t('voice.generatingAudio')
          : t('voice.ready'));

  return (
    <div className="rounded-xl border bg-card shadow-lg p-4 sm:p-6 space-y-4">
      <div className="grid gap-3 lg:grid-cols-[1.1fr_1fr]">
        <div className="rounded-xl border border-border/60 bg-background p-4 sm:p-5 space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm">{t('text.sourceLanguage')}</Label>
              <LanguageSelector value={sourceLanguage} onValueChange={setSourceLanguage} includeAutoDetect />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm">{t('text.targetLanguage')}</Label>
              <LanguageSelector value={targetLanguage} onValueChange={setTargetLanguage} />
            </div>
          </div>

          <OutputModeSelector value={outputMode} onValueChange={setOutputMode} />

          <div className="rounded-2xl border border-primary/20 bg-secondary/30 p-4 sm:p-5 space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="text-sm font-semibold">{t('voice.inputTitle')}</p>
                <p className="text-xs text-muted-foreground">{statusText}</p>
                {capturedDuration !== null ? (
                  <p className="text-xs text-muted-foreground">{t('voice.recordedDuration').replace('{seconds}', capturedDuration.toFixed(1))}</p>
                ) : null}
              </div>
              <div className="rounded-full border border-primary/20 bg-background px-3 py-1 text-xs font-medium text-foreground">
                {new Date(Math.floor(recordingSeconds) * 1000).toISOString().slice(14, 19)}
              </div>
            </div>

            <div className="flex items-center justify-center">
              <Button
                type="button"
                size="icon"
                onClick={recordingState === 'recording' ? stopRecording : startRecording}
                className="h-20 w-20 rounded-full shadow-lg transition-transform hover:scale-105"
                disabled={recordingState === 'transcribing' || recordingState === 'translating' || recordingState === 'generating-audio'}
              >
                {recordingState === 'recording' ? <Square className="h-8 w-8" /> : <Mic className="h-8 w-8" />}
              </Button>
            </div>

            <div className="flex items-end justify-center gap-1.5 h-14">
              {waveformBars.map((bar) => {
                const active = recordingState === 'recording';
                const height = active ? Math.max(16, Math.round((Math.sin(bar + meterLevel * 6) + 1.2) * 18 * Math.max(meterLevel, 0.25))) : 10 + ((bar % 4) * 4);
                return <div key={bar} className="w-2 rounded-full bg-primary/70 transition-all duration-150" style={{ height }} />;
              })}
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} className="flex-1 gap-2 h-11">
                <Upload className="h-4 w-4" />
                {t('voice.uploadAudio')}
              </Button>
              <Button type="button" variant="outline" onClick={requestReset} disabled={!hasContent} className="flex-1 gap-2 h-11">
                <RotateCcw className="h-4 w-4" />
                {t('text.newTranslation')}
              </Button>
            </div>
            <input ref={fileInputRef} type="file" accept="audio/*" className="hidden" onChange={handleAudioUpload} />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <Label className="text-xs sm:text-sm">{t('voice.transcript')}</Label>
              {detectedLanguage && sourceLanguage === 'auto' ? (
                <span className="text-xs text-muted-foreground">{t('text.detectedLanguage').replace('{lang}', getLanguageName(detectedLanguage))}</span>
              ) : null}
            </div>
            <Textarea value={transcript} onChange={(event) => setTranscript(event.target.value)} placeholder={t('voice.transcriptPlaceholder')} className="min-h-[180px] resize-none text-sm sm:text-base" />
          </div>

          <Button onClick={handleTranslate} disabled={!transcript.trim() || ['transcribing', 'translating', 'generating-audio'].includes(recordingState)} size="lg" className="w-full h-12 text-sm sm:text-base gap-2">
            {['transcribing', 'translating', 'generating-audio'].includes(recordingState) ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            {t('voice.translateVoice')}
          </Button>
        </div>

        <div className="rounded-xl border border-border/60 bg-muted/15 p-4 sm:p-5 space-y-4 min-h-[520px]">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-sm font-semibold">{t('voice.outputTitle')}</p>
              <p className="text-xs text-muted-foreground">{isSubscribed ? t('voice.premiumEnabled') : t('voice.basicPlayback')}</p>
            </div>
            {selectedFileName ? <span className="text-xs text-muted-foreground">{selectedFileName}</span> : null}
          </div>

          {(outputMode === 'both' || outputMode === 'text') && (
            <div className="space-y-2 rounded-xl border border-border/60 bg-background p-4">
              {outputMode === 'both' && (
                <div className="space-y-1">
                  <Label className="text-xs">{t('voice.original')}</Label>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{transcript || t('voice.awaitingInput')}</p>
                </div>
              )}
              <div className="space-y-1">
                <Label className="text-xs">{t('voice.translated')}</Label>
                <p className="text-sm whitespace-pre-wrap">{translatedText || t('text.translationPlaceholder')}</p>
              </div>
            </div>
          )}

          {(outputMode === 'voice' || outputMode === 'both') && (
            <div className="space-y-3 rounded-xl border border-border/60 bg-background p-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-secondary-foreground">
                    <AudioLines className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{t('voice.audioOutput')}</p>
                    <p className="text-xs text-muted-foreground">{isSubscribed ? t('voice.highQualityVoice') : t('voice.browserVoiceFallback')}</p>
                  </div>
                </div>
                <span className="text-xs text-muted-foreground">{audioDuration ? `${audioDuration.toFixed(1)}s` : t('voice.shortAudio')}</span>
              </div>

              <div className="flex gap-2">
                <Button type="button" onClick={handlePlayToggle} disabled={!translatedText.trim()} className="flex-1 gap-2 h-11">
                  {audioPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                  {audioPlaying ? t('voice.pauseAudio') : t('voice.playAudio')}
                </Button>
                <Button type="button" variant="outline" onClick={handleDownload} disabled={!translatedText.trim()} className="gap-2 h-11">
                  <Download className="h-4 w-4" />
                  MP3
                </Button>
              </div>

              {!isSubscribed ? <p className="text-xs text-muted-foreground">{t('voice.premiumHint')}</p> : null}
              <audio ref={audioRef} src={audioUrl ?? undefined} preload="auto" className="hidden" />
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('text.resetConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('text.resetConfirmDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('text.keepEditing')}</AlertDialogCancel>
            <AlertDialogAction onClick={resetState}>{t('text.clearAll')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AccessGateModal open={showGateModal} onOpenChange={setShowGateModal} accessState={accessState} />
    </div>
  );
};

export default VoiceTranslation;